import React, { useState, useEffect, useRef } from "react";
import "./SchedulerPage.css";
import { useTranslation } from "../contexts/TranslationContext";

const SchedulerPage = ({ 
  schedule,
  nextActions,
  lastExecutedAction,
  wsClient, 
  connectionId,
  showMessage,
  groupName,
  isOnline
}) => {
  const [scheduleText, setScheduleText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [lastSavedText, setLastSavedText] = useState("");
  const [isNextActionsFlashing, setIsNextActionsFlashing] = useState(false);
  const [isLastExecutedFlashing, setIsLastExecutedFlashing] = useState(false);
  const overlayRef = useRef(null);
  const textareaRef = useRef(null);
  const nextActionsTimeoutRef = useRef(null);
  const lastExecutedTimeoutRef = useRef(null);

  // Translation hook and text
  const { getTranslation } = useTranslation();
  
  // Page texts
  const texts = {
    pageTitle: getTranslation("Scheduler"),
    nextActionsTitle: getTranslation("Next Scheduled Actions"),
    refreshButton: getTranslation("🔄 Refresh"),
    refreshNextActionsTitle: getTranslation("Refresh next actions"),
    refreshLastExecutedTitle: getTranslation("Refresh last executed action"),
    loadingNextActions: getTranslation("⏳ Loading next actions..."),
    lastExecutedTitle: getTranslation("Last Executed Action"),
    loadingLastExecuted: getTranslation("⏳ Loading last executed action..."),
    weeklyEditorTitle: getTranslation("Weekly Schedule Editor"),
    scheduleLoaded: getTranslation("✓ Schedule Loaded"),
    waitingForData: getTranslation("⏳ Waiting for data..."),
    editButton: getTranslation("Edit"),
    resetButton: getTranslation("Reset"),
    resetButtonTitle: getTranslation("Refresh schedule from device"),
    saveButton: getTranslation("Save"),
    cancelButton: getTranslation("Cancel"),
    linesLabel: getTranslation("Lines"),
    charactersLabel: getTranslation("Characters"),
    scheduleEventsLabel: getTranslation("Schedule Events"),
    formatHelpTitle: getTranslation("💡 Format: [day] or [DD-MM-YYYY] followed by HH:MM action"),
    keyboardHelp: getTranslation("⌨️ Ctrl+S to save, Esc to cancel"),
    noConnectionWarning: getTranslation("⚠️ Save disabled - no connection"),
    uploadingSchedule: getTranslation("Uploading schedule..."),
    scheduleUploaded: getTranslation("Schedule uploaded successfully!"),
    scheduleUploadSent: getTranslation("Schedule upload command sent to DuneBugger device"),
    noConnection: getTranslation("No WebSocket connection - schedule not uploaded"),
    nextActionsRequestSent: getTranslation("Next actions request sent"),
    lastExecutedRequestSent: getTranslation("Last executed action request sent"),
    refreshCommandSent: getTranslation("Refresh command sent"),
    scheduleRefreshSent: getTranslation("Schedule refresh command sent"),
    noConnectionRefresh: getTranslation("No connection - cannot refresh"),
    loadingPlaceholder: getTranslation("Loading schedule from device...")
  };

  // Format text for overlay display
  const formatTextForOverlay = (text) => {
    return text.replace(/\[([^\]]+)\]/g, '<span class="bold-title">[$1]</span>');
  };

  // Update overlay when text changes
  const updateOverlay = () => {
    if (overlayRef.current && !isEditing) {
      overlayRef.current.innerHTML = formatTextForOverlay(scheduleText);
    }
  };

  // Sync overlay scroll with textarea
  const handleTextareaScroll = () => {
    if (overlayRef.current && textareaRef.current && !isEditing) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop;
      overlayRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  // Reset component state when device (groupName) changes
  useEffect(() => {
    console.log("SchedulerPage: groupName changed, resetting state:", groupName);
    setScheduleText("");
    setIsEditing(false);
    setSaveStatus("");
    setLastSavedText("");
  }, [groupName]);

  // Handle schedule data when received from WebSocket
  useEffect(() => {
    console.log("SchedulerPage: schedule prop changed:", schedule, "type:", typeof schedule);
    if (schedule) {
      let scheduleString = schedule;
      
      // Handle new object structure with timestamp or legacy string format
      if (typeof schedule === 'object' && schedule.data !== undefined) {
        // New format: { data: actualSchedule, timestamp: number }
        scheduleString = schedule.data;
        if (typeof scheduleString === 'object') {
          scheduleString = JSON.stringify(scheduleString, null, 2);
        } else if (typeof scheduleString !== 'string') {
          scheduleString = String(scheduleString);
        }
      } else if (typeof schedule === 'object') {
        // Legacy object format
        scheduleString = JSON.stringify(schedule, null, 2);
      } else if (typeof schedule !== 'string') {
        // Legacy non-string format
        scheduleString = String(schedule);
      }
      
      console.log("SchedulerPage: Setting schedule text:", scheduleString);
      setScheduleText(scheduleString);
      setLastSavedText(scheduleString);
      setSaveStatus("Schedule loaded from device");
      setTimeout(() => setSaveStatus(""), 3000);
    }
  }, [schedule]);

  // Default schedule template
  const getDefaultScheduleTemplate = () => {
    return `# Configurazione del programma settimanale per DuneBugger
# Formato: HH:MM azione
# Azioni disponibili: 
# - acceso            : in StandBy pronto per essere avviato tramite pulsante
# - acceso_silenzioso : come sopra, ma l'audio è spento o molto basso per non disturbare le celebrazioni)
# - spento            : completamente spento, non risponde al pulsante di avvio
#
# Devono essere specificati programmi per tutti i giorni della settimana
# I giorni sono indicati tra parentesi quadre: [lunedì], [martedì], [mercoledì], [giovedì], [venerdì], [sabato], [domenica]
# Esempio di programma settimanale:
[domenica]
8:00 acceso
...
21:30 spento
...
[sabato]
8:00 acceso
...
20:00 spento

# Fine della configurazione del programma settimanale


# I giorni speciali possono essere aggiunti qui sotto
# Devono essere specificati con la data completa tra parentesi quadre: [DD-MM-YYYY]
# Le azioni seguono lo stesso formato di sopra
# Le date speciali sovrascrivono il programma settimanale per quel giorno specifico

# Esempio date speciali:
[24-12-2025]
8:00 acceso
23:30 acceso_silenzioso
[25-12-2025]
1:30 spento
7:00 acceso
...
`;
  };





  const handleTextChange = (e) => {
    setScheduleText(e.target.value);
  };

  const handleSave = () => {
    setSaveStatus(texts.uploadingSchedule);
    
    if (wsClient && isOnline) {
      // Send the schedule content using scheduler.set command
      wsClient.sendRequest("scheduler.update_schedule", scheduleText, connectionId);
      
      // Show popup message
      if (showMessage) {
        showMessage(texts.scheduleUploadSent, "info");
      }
      
      setSaveStatus(texts.scheduleUploaded);
    } else {
      setSaveStatus(texts.noConnection);
    }
    
    console.log("Sent schedule content:", scheduleText);
    
    // Save the current text as the last saved version
    setLastSavedText(scheduleText);
    setIsEditing(false);
    
    // Clear status after 3 seconds
    setTimeout(() => setSaveStatus(""), 3000);
  };

  const handleCancel = () => {
    // Revert to last saved text, or default template if no saves have been made
    const textToRevert = lastSavedText || getDefaultScheduleTemplate();
    setScheduleText(textToRevert);
    setIsEditing(false);
    setSaveStatus("");
  };

  const handleKeyDown = (e) => {
    if (isEditing && e.ctrlKey && e.key === 's') {
      e.preventDefault();
      if (isOnline) {
        handleSave();
      }
    }
    if (isEditing && e.key === 'Escape') {
      handleCancel();
    }
  };

  // Load next actions from device
  const loadNextActions = (showPopup = true) => {
    if (wsClient && isOnline) {
      wsClient.sendRequest("scheduler.get_next_actions", "null", connectionId);
      if (showMessage && showPopup) {
        showMessage(texts.nextActionsRequestSent, "info");
      }
    }
  };

  // Load last executed action from device
  const loadLastExecutedAction = (showPopup = true) => {
    if (wsClient && isOnline) {
      wsClient.sendRequest("scheduler.get_last_executed_action", "null", connectionId);
      if (showMessage && showPopup) {
        showMessage(texts.lastExecutedRequestSent, "info");
      }
    }
  };

  // Load next actions and last executed action when component mounts or device changes
  useEffect(() => {
    if (wsClient && isOnline) {
      loadNextActions(false);
      loadLastExecutedAction(false);
    }
  }, [wsClient, isOnline, groupName]);

  // Initialize with template if no data (and no schedule prop is available)
  useEffect(() => {
    console.log("SchedulerPage: template check - scheduleText:", !!scheduleText, "lastSavedText:", !!lastSavedText, "schedule prop:", !!schedule);
    if (!scheduleText && !lastSavedText && !schedule) {
      console.log("SchedulerPage: Setting default template");
      setScheduleText(getDefaultScheduleTemplate());
    }
  }, [scheduleText, lastSavedText, schedule]);

  // Flash effect for next actions changes
  useEffect(() => {
    if (nextActions && nextActions.length > 0) {
      // Clear existing timeout
      if (nextActionsTimeoutRef.current) {
        clearTimeout(nextActionsTimeoutRef.current);
      }
      
      // Trigger flash effect
      setIsNextActionsFlashing(true);
      
      // Remove flash after animation completes
      nextActionsTimeoutRef.current = setTimeout(() => {
        setIsNextActionsFlashing(false);
      }, 3000); // 3 seconds for smooth fade
    }

    // Cleanup timeout on unmount
    return () => {
      if (nextActionsTimeoutRef.current) {
        clearTimeout(nextActionsTimeoutRef.current);
      }
    };
  }, [nextActions]);

  // Flash effect for last executed action changes
  useEffect(() => {
    if (lastExecutedAction) {
      // Clear existing timeout
      if (lastExecutedTimeoutRef.current) {
        clearTimeout(lastExecutedTimeoutRef.current);
      }
      
      // Trigger flash effect
      setIsLastExecutedFlashing(true);
      
      // Remove flash after animation completes
      lastExecutedTimeoutRef.current = setTimeout(() => {
        setIsLastExecutedFlashing(false);
      }, 3000); // 3 seconds for smooth fade
    }

    // Cleanup timeout on unmount
    return () => {
      if (lastExecutedTimeoutRef.current) {
        clearTimeout(lastExecutedTimeoutRef.current);
      }
    };
  }, [lastExecutedAction]);

  // Update overlay when text or editing state changes
  useEffect(() => {
    updateOverlay();
  }, [scheduleText, isEditing]);

  return (
    <div className="scheduler-page">
      <h2>{texts.pageTitle}</h2>
      
      {/* Next Actions Section */}
      <div className={`next-actions-section ${isNextActionsFlashing ? 'panel-flash' : ''}`}>
        <div className="next-actions-header">
          <h3>{texts.nextActionsTitle}</h3>
          <button 
            className="refresh-next-actions-button"
            onClick={loadNextActions}
            disabled={!isOnline}
            title={texts.refreshNextActionsTitle}
          >
            {texts.refreshButton}
          </button>
        </div>
        <div className="next-actions-content">
          {nextActions && nextActions.length > 0 ? (
            <div className="actions-grid">
              {nextActions.slice(0, 3).map((action, index) => (
                <div key={index} className="action-card">
                  <div className="action-datetime">
                    <div className="action-date">{action.date}</div>
                    <div className="action-time">{action.time}</div>
                  </div>
                  <div className="action-details">
                    <div className="action-name">{action.action}</div>
                    <div className="action-description">{action.description}</div>
                  </div>
                  <div className="action-commands">
                    {action.commands && action.commands.length > 0 && (
                      <div className="commands-list">
                        {action.commands.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-actions">
              <span className="waiting-message">{texts.loadingNextActions}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Last Executed Action Section */}
      <div className={`last-executed-section ${isLastExecutedFlashing ? 'panel-flash' : ''}`}>
        <div className="last-executed-header">
          <h3>{texts.lastExecutedTitle}</h3>
          <button 
            className="refresh-last-executed-button"
            onClick={loadLastExecutedAction}
            disabled={!isOnline}
            title={texts.refreshLastExecutedTitle}
          >
            {texts.refreshButton}
          </button>
        </div>
        <div className="last-executed-content">
          {lastExecutedAction ? (
            lastExecutedAction.executed ? (
              <div className="executed-action-card">
                <div className="action-datetime">
                  <div className="action-date">{lastExecutedAction.date}</div>
                  <div className="action-time">{lastExecutedAction.time}</div>
                </div>
                <div className="action-details">
                  <div className="action-name">{lastExecutedAction.action}</div>
                  <div className="action-description">{lastExecutedAction.description}</div>
                </div>
                <div className="action-commands">
                  {lastExecutedAction.commands && lastExecutedAction.commands.length > 0 && (
                    <div className="commands-list">
                      {lastExecutedAction.commands.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="no-execution">
                <span className="no-execution-message">💭 {lastExecutedAction.message}</span>
              </div>
            )
          ) : (
            <div className="loading-execution">
              <span className="waiting-message">{texts.loadingLastExecuted}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="scheduler-text-section">
        <div className="text-editor-header">
          <div className="header-left">
            <h3>{texts.weeklyEditorTitle}</h3>
            {lastSavedText ? (
              <span className="data-status loaded">{texts.scheduleLoaded}</span>
            ) : (
              <span className="data-status waiting">{texts.waitingForData}</span>
            )}
          </div>
          <div className="editor-controls">
            {!isEditing ? (
              <>
                <button 
                  className="edit-button"
                  onClick={() => setIsEditing(true)}
                  disabled={!isOnline}
                >
                  {texts.editButton}
                </button>
                <button 
                  className="reset-button"
                  onClick={() => {
                    if (wsClient && isOnline) {
                      wsClient.sendRequest("scheduler.get_schedule", "null", connectionId);
                      setSaveStatus(texts.refreshCommandSent);
                      if (showMessage) {
                        showMessage(texts.scheduleRefreshSent, "info");
                      }
                    } else {
                      setSaveStatus(texts.noConnectionRefresh);
                    }
                    setTimeout(() => setSaveStatus(""), 3000);
                  }}
                  disabled={!isOnline}
                  title={texts.resetButtonTitle}
                >
                  {texts.resetButton}
                </button>
              </>
            ) : (
              <>
                <button 
                  className="save-button"
                  onClick={handleSave}
                  disabled={!scheduleText.trim() || !isOnline}
                >
                  {texts.saveButton}
                </button>
                <button 
                  className="cancel-button"
                  onClick={handleCancel}
                >
                  {texts.cancelButton}
                </button>
              </>
            )}
          </div>
        </div>
        
        {saveStatus && (
          <div className={`save-status ${saveStatus.startsWith('Error') ? 'error' : 'success'}`}>
            {saveStatus}
          </div>
        )}
        
        <div className="textarea-container">
          <div 
            ref={overlayRef}
            className={`text-overlay ${isEditing ? 'hidden' : 'visible'}`}
          />
          <textarea
            ref={textareaRef}
            className={`schedule-text-editor ${isEditing ? 'editing' : 'readonly'}`}
            value={scheduleText}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onScroll={handleTextareaScroll}
            readOnly={!isEditing}
            placeholder={texts.loadingPlaceholder}
            rows={40}
          />
        </div>
        
        <div className="editor-footer">
          <div className="text-stats">
            {texts.linesLabel}: {scheduleText.split('\n').length} | 
            {texts.charactersLabel}: {scheduleText.length} | 
            {texts.scheduleEventsLabel}: {scheduleText.split('\n').filter(line => 
              line.trim() && 
              !line.trim().startsWith('#') && 
              !line.trim().startsWith('[') && 
              !line.trim().startsWith('Fine') &&
              line.includes(':')
            ).length}
          </div>
          {isEditing && (
            <div className="editor-help">
              <small>
                {texts.formatHelpTitle}<br/>
                {texts.keyboardHelp}
                {!isOnline && <><br/>{texts.noConnectionWarning}</>}
              </small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulerPage;